import { auth, functions, db } from "./firebase";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { choiceTimestamp } from "./choiceDate";

import { accountStorage } from "./accountStorage";

const timestampIso = (value) => value?.toDate ? value.toDate().toISOString() : (value || null);

const MAX_CALLS = 4;

const getUid = () => auth.currentUser?.uid || null;

export const callsLeft = (d) => MAX_CALLS - (d?.aiCallsUsed ?? 0);

export const decisionIds = () => accountStorage().read("axoris.cache.decisionIds", []);

export async function listDecisions() {
  const cache = accountStorage();
  const uid = getUid();
  if (!uid) return { decisions: [] };

  try {
    const decsCol = collection(db, "users", uid, "decisions");
    const decsSnap = await getDocs(decsCol);

    const decisions = await Promise.all(
      decsSnap.docs.map(async (docSnap) => {
        const data = docSnap.data();

        // Ambil subkoleksi items untuk reasoning pada useAttention
        const itemsSnap = await getDocs(collection(db, "users", uid, "decisions", docSnap.id, "items"));
        const items = itemsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const updatedAtIso = data.updatedAt?.toDate
          ? data.updatedAt.toDate().toISOString()
          : (data.updatedAt || new Date().toISOString());

        return {
          id: docSnap.id,
          ...data,
          items,
          chooseBy: timestampIso(data.chooseBy),
          reviewAt: timestampIso(data.reviewAt),
          committedAt: data.committedAt?.toDate ? data.committedAt.toDate().toISOString() : (data.committedAt || null),
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : (data.createdAt || new Date().toISOString()),
          updatedAt: updatedAtIso,
        };
      })
    );

    // Urutkan berdasarkan updatedAt menurun
    decisions.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

    if (!cache.isCurrent()) return { error: "read" };
    cache.write("axoris.cache.decisionIds", decisions.map((d) => d.id));
    return { decisions };
  } catch {
    return { error: "read" };
  }
}

export async function getDecision(id) {
  if (!id) return { error: "missing" };
  const uid = getUid();
  if (!uid) return { error: "missing" };

  try {
    const decRef = doc(db, "users", uid, "decisions", id);
    const snap = await getDoc(decRef);
    if (!snap.exists()) {
      return { error: "missing" };
    }

    const data = snap.data();

    // Baca subkoleksi items
    const itemsCol = collection(db, "users", uid, "decisions", id, "items");
    const itemsSnap = await getDocs(itemsCol);
    const items = itemsSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        if (a.n && b.n) return a.n - b.n;
        return (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0);
      });

    // Baca subkoleksi options
    const optsCol = collection(db, "users", uid, "decisions", id, "options");
    const optsSnap = await getDocs(optsCol);
    const options = optsSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0));

    // Baca subkoleksi briefs (jika ada)
    const briefsCol = collection(db, "users", uid, "decisions", id, "briefs");
    const briefsSnap = await getDocs(briefsCol);
    const briefs = briefsSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.version || 0) - (a.version || 0));
    const committed = data.status === "committed" || data.status === "deferred";
    const activeBrief = briefs.find((b) => b.cycle === data.cycle) || (committed ? briefs[0] : null);
    const brief = activeBrief
      ? {
          ...activeBrief,
          ...(activeBrief.content || {}),
          triggerChecklist: (activeBrief.triggerChecklist || []).map((entry) => ({
            text: entry.text, checkedAt: timestampIso(entry.checkedAt),
          })),
        }
      : (committed || (Number.isInteger(data.cycle) && data.brief?.cycle === data.cycle)
          ? (data.brief ?? null)
          : null);

    const assembled = {
      id: snap.id,
      ...data,
      items,
      options,
      chooseBy: timestampIso(data.chooseBy),
      reviewAt: timestampIso(data.reviewAt),
      brief,
      attachments: data.attachments || [],
      continuity: data.continuity || null,
      committedAt: data.committedAt?.toDate ? data.committedAt.toDate().toISOString() : (data.committedAt || null),
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : (data.createdAt || new Date().toISOString()),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : (data.updatedAt || new Date().toISOString()),
    };

    return { decision: assembled };
  } catch {
    return { error: "missing" };
  }
}

export async function createDecision({ title, question, desiredOutcome, horizonDays, tags, chooseBy }) {
  try {
    const fn = httpsCallable(functions, "createDecision");
    const payload = {
      title: title.trim(),
      question: question.trim(),
      desiredOutcome: desiredOutcome?.trim() || "",
      horizonDays: Number(horizonDays),
    };
    if (tags && Array.isArray(tags)) payload.tags = tags;
    if (chooseBy !== undefined) payload.chooseBy = choiceTimestamp(chooseBy);
    const res = await fn(payload);

    if (res.data?.ok && res.data?.decisionId) {
      return await getDecision(res.data.decisionId);
    }
    return { error: "write" };
  } catch (err) {
    if (err?.code === "functions/permission-denied" || err?.message?.includes("Entitlement")) {
      return { error: "locked" };
    }
    return { error: "write" };
  }
}

export async function updateDecision(id, f) {
  try {
    const fn = httpsCallable(functions, "updateDecision");
    const payload = {
      decisionId: id,
      title: f.title.trim(),
      question: f.question.trim(),
      desiredOutcome: f.desiredOutcome?.trim() || "",
      horizonDays: Number(f.horizonDays),
    };
    if (f.tags && Array.isArray(f.tags)) payload.tags = f.tags;
    if (f.chooseBy !== undefined) payload.chooseBy = choiceTimestamp(f.chooseBy);
    const res = await fn(payload);

    if (res.data?.ok) {
      return await getDecision(id);
    }
    return { error: "write" };
  } catch {
    return { error: "write" };
  }
}

export async function saveNote(id, note) {
  try {
    const fn = httpsCallable(functions, "saveNote");
    const res = await fn({
      decisionId: id,
      note: note.slice(0, 2000),
    });

    if (res.data?.ok) {
      return await getDecision(id);
    }
    return { error: "write" };
  } catch {
    return { error: "write" };
  }
}

export async function setChooseBy(id, day) {
  try {
    const res = await httpsCallable(functions, "setChooseBy")({ decisionId: id, chooseBy: choiceTimestamp(day) });
    return res.data?.ok ? await getDecision(id) : { error: "write" };
  } catch {
    return { error: "write" };
  }
}

export async function setBriefTriggerChecked(id, briefId, index, checked) {
  try {
    const res = await httpsCallable(functions, "setBriefTriggerChecked")({ decisionId: id, briefId, index, checked });
    return res.data?.ok ? await getDecision(id) : { error: "write" };
  } catch {
    return { error: "write" };
  }
}

export async function intakeAssist(id) {
  try {
    const fn = httpsCallable(functions, "intakeAssist");
    const res = await fn({ decisionId: id });
    const decRes = await getDecision(id);
    return {
      decision: decRes.decision,
      suggestions: res.data?.suggestions || [],
    };
  } catch {
    return { error: "ai" };
  }
}

export async function addItem(id, { text, epistemic, source }) {
  try {
    const fn = httpsCallable(functions, "addItem");
    const res = await fn({
      decisionId: id,
      text: text.trim(),
      epistemic,
      source: source || "user",
    });

    if (res.data?.ok) {
      return await getDecision(id);
    }
    return { error: "write" };
  } catch {
    return { error: "write" };
  }
}

export async function setItemStatus(id, itemId, epistemic) {
  try {
    const fn = httpsCallable(functions, "setItemStatus");
    const res = await fn({
      decisionId: id,
      itemId,
      epistemic,
    });

    if (res.data?.ok) {
      return await getDecision(id);
    }
    return { error: "write" };
  } catch {
    return { error: "write" };
  }
}

export async function discardItem(id, itemId) {
  try {
    const fn = httpsCallable(functions, "discardItem");
    const res = await fn({
      decisionId: id,
      itemId,
    });

    if (res.data?.ok) {
      return await getDecision(id);
    }
    return { error: "write" };
  } catch {
    return { error: "write" };
  }
}

export async function addOption(id, { name, description, reversibility }) {
  try {
    const fn = httpsCallable(functions, "addOption");
    const res = await fn({
      decisionId: id,
      name: name.trim(),
      description: description?.trim() || "",
      reversibility,
    });

    if (res.data?.ok) {
      return await getDecision(id);
    }
    return { error: "write" };
  } catch {
    return { error: "write" };
  }
}

export async function updateOption(id, { optionId, name, description, reversibility }) {
  try {
    const fn = httpsCallable(functions, "updateOption");
    const res = await fn({
      decisionId: id,
      optionId,
      name: name.trim(),
      description: description?.trim() || "",
      reversibility,
    });

    if (res.data?.ok) {
      return await getDecision(id);
    }
    return { error: "write" };
  } catch {
    return { error: "write" };
  }
}

export async function deleteOption(id, optionId) {
  try {
    const fn = httpsCallable(functions, "deleteOption");
    const res = await fn({
      decisionId: id,
      optionId,
    });

    if (res.data?.ok) {
      return await getDecision(id);
    }
    return { error: "write" };
  } catch {
    return { error: "write" };
  }
}

export async function togglePin(id) {
  try {
    const fn = httpsCallable(functions, "pinAttention");
    const res = await fn({ decisionId: id });
    if (res.data?.ok) {
      return await getDecision(id);
    }
    return { error: "write" };
  } catch (err) {
    if (
      err?.code === "functions/resource-exhausted" ||
      err?.message?.includes("Maximum 3") ||
      err?.details?.includes?.("Maximum 3")
    ) {
      return { error: "maxpin" };
    }
    return { error: "write" };
  }
}

export async function composeBrief(id, lang = "id") {
  try {
    const fn = httpsCallable(functions, "composeBrief");
    const res = await fn({ decisionId: id, lang });
    if (res.data?.ok) {
      return await getDecision(id);
    }
    return { error: "ai" };
  } catch (err) {
    if (
      err?.code === "functions/resource-exhausted" ||
      err?.message?.includes("Quota exceeded") ||
      err?.details?.includes?.("Quota exceeded")
    ) {
      return { error: "quota" };
    }
    return { error: "ai" };
  }
}

export async function runChallenge(id, mode, lang = "id") {
  try {
    const fn = httpsCallable(functions, "challenge");
    const res = await fn({ decisionId: id, mode, lang });
    if (res.data?.ok) {
      return await getDecision(id);
    }
    return { error: "ai" };
  } catch (err) {
    if (
      err?.code === "functions/resource-exhausted" ||
      err?.message?.includes("Quota exceeded") ||
      err?.details?.includes?.("Quota exceeded")
    ) {
      return { error: "quota" };
    }
    return { error: "ai" };
  }
}

export async function checkContinuity(id, lang = "id") {
  try {
    const fn = httpsCallable(functions, "continuityCheck");
    const res = await fn({ decisionId: id, lang });
    if (res.data?.ok) {
      return await getDecision(id);
    }
    return { error: "ai" };
  } catch (err) {
    if (
      err?.code === "functions/resource-exhausted" ||
      err?.message?.includes("Quota exceeded") ||
      err?.details?.includes?.("Quota exceeded")
    ) {
      return { error: "quota" };
    }
    return { error: "ai" };
  }
}

export async function commitDecision(id, { optionId, rationale, nextAction, reviewAt }) {
  try {
    const fn = httpsCallable(functions, "commitDecision");
    const res = await fn({
      decisionId: id,
      optionId,
      deferred: optionId === null,
      rationale: rationale.trim(),
      nextAction: nextAction?.trim() || "",
      reviewAt: new Date(reviewAt).toISOString(),
    });

    if (res.data?.ok) {
      return await getDecision(id);
    }
    return { error: "write" };
  } catch {
    return { error: "write" };
  }
}

export async function setDecisionTags(id, tags) {
  try {
    const fn = httpsCallable(functions, "setDecisionTags");
    const res = await fn({ decisionId: id, tags });
    if (res.data?.ok) {
      return await getDecision(id);
    }
    return { error: "write" };
  } catch {
    return { error: "write" };
  }
}

export async function getRelatedChapters(decisionId) {
  try {
    const fn = httpsCallable(functions, "getRelatedChapters");
    const res = await fn({ decisionId });
    if (res.data?.ok) {
      return {
        chapters: res.data.chapters || [],
        locked: !!res.data.locked,
      };
    }
    return { error: "read" };
  } catch (err) {
    console.error("getRelatedChapters error:", err);
    return { error: "read" };
  }
}

